import React, {useState, useEffect} from 'react';
import {PanelMenu} from 'primereact/panelmenu';
import { useLocation, useHistory } from 'react-router-dom';
import UserProfile from '../../profile/Userprofile';
import StorageHelper from '../../../../utils/storage-helper';

const POLICY_GROUP_LABELS = new Set(['POLICY MANAGEMENT', 'POLICY', 'APPROVALS', 'MANAGE POLICY']);
const ADMIN_ROLES = new Set(['ADMIN', 'UNDERWRITER', 'SUPER_ADMIN']);

const isPathActive = (currentPath, path) => currentPath === path || currentPath.startsWith(path + '/');

const GroupTemplate = ({item, options, isGroupActive}) => (
    <button
        type="button"
        className={`w-full text-left cursor-pointer px-4 py-3 flex items-center justify-between transition-colors ${isGroupActive ? 'text-primary font-bold bg-primary/5' : 'text-base-content/90 font-semibold hover:bg-base-200'}`}
        onClick={options.onClick}
    >
        <div className="flex items-center">
            <i className={`mr-3 ${item.icon}`}></i>
            <span className="text-sm uppercase tracking-wider">{item.label}</span>
        </div>
        <i className={`pi ${options.expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-xs opacity-70`}></i>
    </button>
);

const LinkTemplate = ({item, options, isActive, onNavigate}) => (
    <button
        type="button"
        className={`w-full text-left cursor-pointer px-4 py-3 flex items-center transition-colors ${isActive ? 'text-primary font-bold bg-primary/5' : 'text-base-content/80 hover:bg-base-200'}`}
        onClick={(e) => {
            options.onClick?.(e);
            onNavigate();
        }}
    >
        <i className={`mr-3 ${item.icon}`}></i>
        <span className="text-sm">{item.label}</span>
    </button>
);

const buildFunctionItem = (functionItem, currentPath, history) => {
    const actionPath = functionItem.action ? functionItem.action.replace(/\/$/, '') : '';
    const isActive = isPathActive(currentPath, actionPath);
    return {
        label: functionItem.name,
        icon: 'pi pi-angle-right',
        className: isActive ? 'bg-primary/10 text-primary font-bold border-r-4 border-primary' : '',
        isActive,
        command: () => {
            // Use React Router for navigation to prevent full page reloads
            if (functionItem.action) {
                history.push(functionItem.action);
            }
        }
    };
};

const buildGroup = (permissionsGroup, currentPath, history) => {
    const systemFunctionItems = (permissionsGroup.systemFunctions || [])
        .map((functionItem) => buildFunctionItem(functionItem, currentPath, history));
    // Auto-expand the parent group if a child is active
    const isGroupActive = systemFunctionItems.some((item) => item.isActive);

    return {
        label: permissionsGroup.groupName,
        icon: permissionsGroup.groupIcons || 'pi pi-folder',
        expanded: isGroupActive,
        className: isGroupActive ? 'text-primary font-bold' : '',
        template: (item, options) => <GroupTemplate item={item} options={options} isGroupActive={isGroupActive}/>,
        items: systemFunctionItems,
    };
};

const buildLinkItem = ({label, icon, path, currentPath, history, activeClass}) => {
    const isActive = isPathActive(currentPath, path);
    return {
        isActive,
        item: {
            label,
            icon,
            className: isActive ? activeClass : '',
            template: (item, options) => (
                <LinkTemplate item={item} options={options} isActive={isActive} onNavigate={() => history.push(path)}/>
            )
        }
    };
};

// Adds `newItem` to `group` unless an item with the same label is already there.
const addToGroup = (group, newItem, isActive) => {
    if (!group.items.some((subItem) => subItem.label === newItem.label)) {
        group.items.push(newItem);
        if (isActive) group.expanded = true;
    }
};

const addAdminItems = (updatedItems, currentPath, history) => {
    const bc = buildLinkItem({
        label: 'Beneficiary Change Requests', icon: 'pi pi-angle-right', path: '/policy/beneficiary-requests',
        currentPath, history, activeClass: 'bg-primary/10 border-r-4 border-primary'
    });
    const claims = buildLinkItem({
        label: 'View All Claims', icon: 'pi pi-list', path: '/claims/view-all',
        currentPath, history, activeClass: 'bg-primary/10 border-r-4 border-primary'
    });

    const policyGroup = updatedItems.find((item) => POLICY_GROUP_LABELS.has(item.label.toUpperCase()));

    if (policyGroup) {
        addToGroup(policyGroup, bc.item, bc.isActive);
        addToGroup(policyGroup, claims.item, claims.isActive);
        return;
    }

    const approvalsGroup = {
        label: 'Review Approvals',
        icon: 'pi pi-check-square',
        expanded: bc.isActive,
        items: [bc.item]
    };
    updatedItems.push(approvalsGroup);
    if (claims.isActive) approvalsGroup.expanded = true;
    approvalsGroup.items.push(claims.item);
};

const buildSidebarItems = (userRole, currentPath, history) => {
    const updatedItems = userRole.permissionsGroups
        .map((permissionsGroup) => buildGroup(permissionsGroup, currentPath, history));

    const roleName = userRole.name || '';
    if (ADMIN_ROLES.has(roleName.toUpperCase())) {
        addAdminItems(updatedItems, currentPath, history);
    }
    return updatedItems;
};

const Sidebar1 = () => {

    StorageHelper.set('sideBarHidden', 'false');

    const [isSidebarVisible, setSidebarVisible] = useState(
        StorageHelper.get('sideBarHidden') !== 'true'
    );
    const [items, setItems] = useState([]);
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        const fetchUserPermissions = async () => {
            try {
                const userRole = StorageHelper.get('userRole');
                if (!userRole?.permissionsGroups) {
                    console.warn('User role or permissions not found');
                    return;
                }

                const currentPath = window.location.pathname.replace(/\/$/, ''); // normalize trailing slash
                setItems(buildSidebarItems(userRole, currentPath, history));
            } catch (error) {
                console.error('Error fetching user permissions:', error);
            }
        };
        fetchUserPermissions();
    }, [location.pathname, history]);

    useEffect(() => {
        StorageHelper.set('sideBarHidden', !isSidebarVisible);
    }, [isSidebarVisible]);

    useEffect(() => {
        // Handler for storage event
        const handleStorageChange = (event) => {
            if (event.key === 'sideBarHidden') {
                setSidebarVisible(event.newValue !== 'true');
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        if (isSidebarVisible) {
            document.body.classList.add('has-admin-sidebar');
        } else {
            document.body.classList.remove('has-admin-sidebar');
        }
        return () => document.body.classList.remove('has-admin-sidebar');
    }, [isSidebarVisible]);

    return (
        <>
            {isSidebarVisible && (
                <div className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-72 bg-base-100 border-r border-base-200 shadow-sm z-[90] overflow-y-auto flex flex-col transition-all duration-300">
                    <div className="p-4 border-b border-base-200 flex justify-between items-center bg-base-50">
                        <span className="font-bold text-base-content/70 uppercase text-xs tracking-wider">Navigation</span>
                        <button 
                            className="btn btn-ghost btn-xs btn-circle text-base-content/50 hover:text-primary hover:bg-primary/10" 
                            onClick={() => setSidebarVisible(false)}
                            title="Hide Sidebar"
                        >
                            <i className="pi pi-angle-left text-lg"></i>
                        </button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2">
                        <PanelMenu model={items} multiple={false} className="w-full border-none" />
                    </div>

                    <div className="p-4 border-t border-base-200 mt-auto bg-base-50">
                        <UserProfile />
                    </div>
                </div>
            )}

            {!isSidebarVisible && (
                <div className="fixed left-0 top-20 z-[95]">
                    <button 
                        className="btn btn-primary rounded-r-full rounded-l-none shadow-md"
                        onClick={() => setSidebarVisible(true)}
                        title="Show Sidebar"
                    >
                        <i className="pi pi-bars"></i>
                    </button>
                </div>
            )}
        </>
    );
};

export default Sidebar1;
